import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Navbar } from '../components';
import { paymentsAPI } from '../services/api';
import './PlanSelectionPage.css';

const PLANS = [
  {
    id: 20,
    interval: 'monthly',
    label: 'Monthly',
    price: '$19.99',
    period: '/month',
    badge: null,
    tagline: 'Full access, billed monthly.',
  },
  {
    id: 21,
    interval: 'yearly',
    label: 'Annual',
    price: '$149.99',
    period: '/year',
    badge: 'Best Value',
    tagline: 'Save over 37% — billed once a year.',
  },
];

const PlanSelectionPage = () => {
  const navigate = useNavigate();
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  // Holds cleanup logic (remove message listener + clear poll interval) for the active popup
  const cleanupRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/signin', { replace: true });
      return;
    }

    // If user already has an active subscription, skip to create-avatar
    const checkSub = async () => {
      try {
        const apiBase = (process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api').replace(/\/api$/, '');
        const res = await fetch(`${apiBase}/api/addons/my-plan`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          navigate('/create-avatar', { replace: true });
          return;
        }
        if (res.status === 401 || res.status === 403) {
          // Token expired or invalid — send back to sign in
          localStorage.removeItem('access_token');
          navigate('/signin', { replace: true });
          return;
        }
        // 404 = no active subscription → show plan selection
      } catch (_) { }
      setCheckingSubscription(false);
    };
    checkSub();
  }, [navigate]);

  // Block browser back/forward — user must complete payment to proceed
  useEffect(() => {
    const onPopState = () => {
      window.history.pushState(null, '', window.location.pathname);
    };
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Clean up popup listeners when the component unmounts
  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  const handleSelectPlan = async (plan) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/signin', { replace: true });
      return;
    }

    setLoadingPlanId(plan.id);
    try {
      const res = await paymentsAPI.createPaypalOrder({
        avatar_plan_id: plan.id,
        plan_interval: plan.interval,
      });

      const data = res?.data || res;
      const approveUrl = data?.approve_url;
      if (!approveUrl) {
        toast.error('Could not start payment. Please try again.');
        setLoadingPlanId(null);
        return;
      }

      // Store checkout context so PaymentSuccessPage can capture the order.
      // context: 'popup' tells PaymentSuccessPage to post a message back and close.
      const ctx = {
        avatar_plan_id: plan.id,
        plan_interval: plan.interval,
        auto_renew: true,
        started_from: '/create-avatar',
        context: 'popup',
      };
      sessionStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));
      localStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));

      // Centre the popup on the user's screen
      const popupWidth = 500;
      const popupHeight = 700;
      const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);

      const popup = window.open(
        approveUrl,
        'PayPal',
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      // Popup was blocked by the browser — fall back to full-page redirect
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        toast.info('Popup was blocked. Redirecting to PayPal…');
        window.location.href = approveUrl;
        return;
      }

      setPopupOpen(true);

      // ── Message handler ──────────────────────────────────────────────────
      // PaymentSuccessPage posts PAYPAL_SUCCESS or PAYPAL_ERROR then closes itself.
      const messageHandler = (event) => {
        if (event.data?.type === 'PAYPAL_SUCCESS') {
          if (cleanupRef.current) cleanupRef.current();
          navigate('/create-avatar', { replace: true });
        } else if (event.data?.type === 'PAYPAL_ERROR') {
          if (cleanupRef.current) cleanupRef.current();
          toast.error('Payment failed. Please try again.');
          setLoadingPlanId(null);
          setPopupOpen(false);
        }
      };

      // ── Poll for manual popup closure (user cancelled / closed the window) ──
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          if (cleanupRef.current) cleanupRef.current();
          setLoadingPlanId(null);
          setPopupOpen(false);
        }
      }, 600);

      cleanupRef.current = () => {
        clearInterval(pollTimer);
        window.removeEventListener('message', messageHandler);
        cleanupRef.current = null;
      };

      window.addEventListener('message', messageHandler);
    } catch (err) {
      console.error('PayPal create order failed:', err);
      if (err?.status === 401 || err?.statusCode === 401) {
        localStorage.removeItem('access_token');
        navigate('/signin', { replace: true });
        return;
      }
      toast.error(err?.message || 'Payment initiation failed. Please try again.');
      setLoadingPlanId(null);
    }
  };

  if (checkingSubscription) {
    return (
      <div className="plan-selection-page">
        <Navbar disabled={true} navClassName="navbar-beige" />
        <div className="plan-selection-loading">
          <div className="spinner-border-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="plan-selection-page">
      <Navbar disabled={true} />

      <main className="plan-selection-main">
        <div className="plan-selection-container">
          <ul className="plan-shared-features">
            <li>Invite friends and family to connect with the Avatar you create</li>
            <li>Create multiple Avatars within one Family Group</li>
            <li>Add new documents and memories at any time</li>
            <li>Secure, private storage for everything you share</li>
          </ul>

          <div className="plan-cards">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`plan-card${plan.badge ? ' plan-card--featured' : ''}`}
              >
                {plan.badge && (
                  <div className="plan-card__badge">{plan.badge}</div>
                )}
                <div className="plan-card__label">{plan.label}</div>
                <div className="plan-card__price">
                  <span className="plan-card__price-amount">{plan.price}</span>
                  <span className="plan-card__price-period">{plan.period}</span>
                </div>
                <p className="plan-card__tagline">{plan.tagline}</p>

                <button
                  className="plan-card__cta"
                  onClick={() => handleSelectPlan(plan)}
                  disabled={loadingPlanId !== null}
                >
                  {loadingPlanId === plan.id
                    ? (popupOpen ? 'Complete payment in popup…' : 'Starting…')
                    : 'Get Started'}
                </button>
              </div>
            ))}
          </div>

          {popupOpen && (
            <p className="plan-selection-popup-hint">
              A PayPal window has opened — complete your payment there to continue.
            </p>
          )}

          <p className="plan-selection-footer">
            Payments are processed securely via PayPal. You can cancel anytime.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PlanSelectionPage;
